import { isDefined } from '@aloft/toolkit-shared';

import { countBy } from 'lodash';

import { useVacantLandSales } from '../../../hooks/useVacantLandSales.tsx';
import {
  ExponentialFitDataPoint,
  GroupedVacantLandSalesChartData,
  VacantLandSalesChartData,
} from './types.ts';

export const getVacantLandChartData = (
  vacantLandSales: ReturnType<typeof useVacantLandSales>,
): VacantLandSalesChartData[] | undefined => {
  if (!vacantLandSales) return undefined;

  const vacantLandSalesChartData = vacantLandSales
    .map(({ address, distanceToSubject, lotSize, saleDate, salePrice }) => {
      // Filter out invalid data and sales less than $5000
      if (!lotSize || !salePrice || salePrice.toDollarLike() < 5000) return undefined;

      const costPerSF = salePrice.div(lotSize.getUnitAmount('sqft')).toDollarLike();

      return {
        address: address?.AddressLineText,
        saleDate: saleDate?.toString(),
        siteSize: lotSize?.getUnitAmount('sqft'),
        costPerSF,
        distanceToSubject,
      };
    })
    .filter(isDefined);

  // Winsorize: Removes values outside of the 5th and 95th percentile range to reduce outlier impact on graphs
  const costsPerSF = vacantLandSalesChartData.map((s) => s.costPerSF).sort((a, b) => a - b);
  const p05 = costsPerSF[Math.floor(costsPerSF.length * 0.05)]!;
  const p95 = costsPerSF[Math.floor(costsPerSF.length * 0.95)]!;

  return vacantLandSalesChartData.filter((sale) => sale.costPerSF >= p05 && sale.costPerSF <= p95);
};

export const groupVacantLandSalesData = (
  data: VacantLandSalesChartData[],
): GroupedVacantLandSalesChartData[] => {
  const totalCount = data.length;

  const counts = countBy(
    data.map((d) => d.costPerSF),
    Math.floor,
  );

  const groups = Object.entries(counts).map(([costPerSF, count]) => ({
    costPerSF: Number(costPerSF),
    percentage: Number(((count / totalCount) * 100).toFixed(2)),
  }));

  return groups;
};

const SCALE = 10000; // Scale down values to improve numerical stability

/**
 * Fits an exponential curve to vacant land sales data: cost per SF vs site size (sqft).
 *
 * Formula: y = a * exp(b * x)
 *   - x: site size (sqft)
 *   - y: cost per SF
 *   - a, b: calculated from the data so the curve passes through the subject point
 *
 * Returns:
 *   - `lineData`: curve points for charts
 *   - `getCostPerSFFromSiteSize(siteSize)`: function that gives the fitted cost/SF for a given size
 *
 * Expects: filtered, valid sales data (no zeros or negatives).
 */
export const getVacantLandSalesExponentialFit = (
  vacantLandSalesData: VacantLandSalesChartData[],
): {
  lineData: ExponentialFitDataPoint[];
  getCostPerSFFromSiteSize: (siteSize: number) => number;
} => {
  const safeExp = (v: number) => Math.exp(Math.min(v, Math.log(Number.MAX_VALUE))); // Safe JS limit for exponentiation

  const siteSizes = vacantLandSalesData.map((d) => d.siteSize);
  const scaledSiteSizes = siteSizes.map((size) => size / SCALE);

  const xValues = scaledSiteSizes;
  const yValues = vacantLandSalesData.map((d) => d.costPerSF);
  const logYValues = yValues.map((y) => Math.log(y));

  const meanX = xValues.reduce((sum, val) => sum + val, 0) / xValues.length;
  const meanY = logYValues.reduce((sum, val) => sum + val, 0) / logYValues.length;

  const covXY =
    xValues.reduce((sum, x, i) => sum + (x - meanX) * (logYValues[i]! - meanY), 0) / xValues.length;

  const varX = xValues.reduce((sum, x) => sum + (x - meanX) ** 2, 0) / xValues.length;

  let b = covXY / varX;
  b = Number.isNaN(b) ? 0 : b;
  let a = Math.exp(meanY - b * meanX);
  a = Number.isNaN(a) ? 0 : a;

  const minX = 0;
  const maxX = Math.max(...siteSizes);
  const numPoints = 50;
  const step = (maxX - minX) / (numPoints - 1);

  const lineData = Array.from({ length: numPoints }, (_, i) => {
    const x = minX + i * step;

    return {
      siteSize: x,
      lineY: a * safeExp(b * (x / SCALE)),
    };
  });

  return {
    lineData,
    getCostPerSFFromSiteSize: (siteSize: number) => a * safeExp(b * (siteSize / SCALE)),
  };
};

export const sortBySiteSize = <T extends { siteSize?: number }>(arr: T[]): T[] =>
  [...arr].sort((a, b) => (a.siteSize ?? 0) - (b.siteSize ?? 0));
